/**
 * notifications.js — Moteur notifications intelligentes FG-OS
 *
 * Scanne prospects + devis + CA pour generer des notifs deduplicees :
 *   - Relance prospect J+5 (statut devis_envoye, dateDernierStatut > 5j)
 *   - Relance devis J+7 (devis statut envoye, dateEnvoi > 7j)
 *   - Audit sans devis > 24h (statut audite, pas de devis lie au prospect)
 *   - Palier CA a portee (manque <= 2000 EUR)
 *   - Reassort J-7 (SPEC-04 : date de reassort predite dans <= 7 jours)
 *
 * Dedupe par dedupKey stable (par ressource + type), pas de spam au refresh.
 * Appelle addNotification() qui ignore si dedupKey deja present non-dismissed.
 */

import {
  addNotification,
  cleanExpiredNotifications,
  getAllNotificationsRaw,
  getProspects,
  listDevis,
  getCANonFG,
} from './storage.js';
import { calcPaliersFranchis, PALIERS_CA } from './pricing.js';
import { getReassortsAVenir } from './reassort.js';

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

function isCurrentMonth(iso, now) {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function computeCATotal(prospects, now) {
  const caNonFG = getCANonFG().montant || 0;
  const STATUTS_FACTURES = ['devis_envoye', 'compte_ouvert', 'recurrent'];
  const caFG = prospects
    .filter((p) => STATUTS_FACTURES.includes(p.statut))
    .filter((p) => p.devis && isCurrentMonth(p.devis.dateEnvoi, now))
    .reduce((sum, p) => sum + (Number(p.devis.montantHT) || 0), 0);
  return caNonFG + caFG;
}

export function checkAndGenerateNotifications() {
  cleanExpiredNotifications();

  const existing = getAllNotificationsRaw();
  const activeKeys = new Set(
    existing.filter((n) => !n.dismissed).map((n) => n.dedupKey),
  );

  const prospects = getProspects();
  const devis = listDevis();
  const now = new Date();
  const nowMs = now.getTime();
  const generated = [];

  // 1. Relance prospect J+5
  for (const p of prospects) {
    if (p.statut !== 'devis_envoye') continue;
    const dateRef = p.dateDernierStatut ? new Date(p.dateDernierStatut).getTime() : null;
    if (!dateRef) continue;
    const ageDays = (nowMs - dateRef) / DAY_MS;
    if (ageDays < 5) continue;
    const key = `relance-prospect-j5:${p.id}`;
    if (activeKeys.has(key)) continue;
    const n = addNotification({
      type: 'relance_prospect_j5',
      title: `Relancer ${p.nom}`,
      message: `Devis envoye il y a ${Math.floor(ageDays)} jours, pas de retour.`,
      severity: 'warning',
      navigate: { module: 'redaction', context: { prospectId: p.id, type: 'relance' } },
      dedupKey: key,
      prospectId: p.id,
    });
    if (n) generated.push(n);
  }

  // 2. Relance devis J+7
  for (const d of devis) {
    if (d.statut !== 'envoye') continue;
    const dateEnvoi = d.dateEnvoi
      ? new Date(d.dateEnvoi).getTime()
      : d.updatedAt
        ? new Date(d.updatedAt).getTime()
        : null;
    if (!dateEnvoi) continue;
    const ageDays = (nowMs - dateEnvoi) / DAY_MS;
    if (ageDays < 7) continue;
    const key = `relance-devis-j7:${d.id}`;
    if (activeKeys.has(key)) continue;
    const n = addNotification({
      type: 'relance_devis_j7',
      title: `Devis ${d.numero || ''} sans reponse`,
      message: `${d.clientName} - envoye il y a ${Math.floor(ageDays)} jours.`,
      severity: 'warning',
      navigate: { module: 'chiffrage', context: { devisId: d.id } },
      dedupKey: key,
      devisId: d.id,
    });
    if (n) generated.push(n);
  }

  // 3. Audit sans devis > 24h
  for (const p of prospects) {
    if (p.statut !== 'audite') continue;
    const dateRef = p.dateDernierStatut ? new Date(p.dateDernierStatut).getTime() : null;
    if (!dateRef) continue;
    const ageHours = (nowMs - dateRef) / HOUR_MS;
    if (ageHours < 24) continue;
    const hasDevis = devis.some((d) => d.prospectId === p.id);
    if (hasDevis) continue;
    const key = `audit-sans-devis:${p.id}`;
    if (activeKeys.has(key)) continue;
    const n = addNotification({
      type: 'audit_sans_devis',
      title: `Chiffrer ${p.nom}`,
      message: `Audite il y a plus de 24h, pas de devis cree.`,
      severity: 'info',
      navigate: { module: 'chiffrage', context: { prospectId: p.id } },
      dedupKey: key,
      prospectId: p.id,
    });
    if (n) generated.push(n);
  }

  // 4. Palier CA a portee (manque <= 2000)
  const caTotal = computeCATotal(prospects, now);
  const { nbFranchis } = calcPaliersFranchis(caTotal);
  if (nbFranchis < PALIERS_CA.length) {
    const next = PALIERS_CA[nbFranchis];
    const manque = next - caTotal;
    if (manque > 0 && manque <= 2000) {
      const monthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      const key = `palier-proche:${next}:${monthKey}`;
      if (!activeKeys.has(key)) {
        const n = addNotification({
          type: 'palier_proche',
          title: `Palier ${(next / 1000).toFixed(0)}k a portee`,
          message: `Manque ${Math.round(manque)} EUR pour declencher 300 EUR de prime.`,
          severity: 'success',
          navigate: { module: 'dashboard' },
          dedupKey: key,
          expireDays: 7,
        });
        if (n) generated.push(n);
      }
    }
  }

  // 5. Reassort J-7 (SPEC-04) : la date predite approche, relancer AVANT la panne
  for (const { prospect, prediction } of getReassortsAVenir({ horizonJours: 7, aujourdhui: now })) {
    const dateIso = prediction.dateReassort.toISOString().slice(0, 10);
    const key = `reassort-j7:${prospect.id}:${prediction.ref_interne}:${dateIso}`;
    if (activeKeys.has(key)) continue;
    const estime = prediction.estimation ? ' (estimation approximative)' : '';
    const n = addNotification({
      type: 'reassort_j7',
      title: `Reassort ${prospect.nom}`,
      message: `${prediction.ref_interne} x${prediction.quantite} — a court vers le ${dateIso}${estime}.`,
      severity: 'warning',
      navigate: { module: 'redaction', context: { prospectId: prospect.id, type: 'relance_reassort' } },
      dedupKey: key,
      prospectId: prospect.id,
      expireDays: 14,
    });
    if (n) generated.push(n);
  }

  return generated;
}

/**
 * Trigger ponctuel sur changement statut Pipeline. Add direct (pas de scan).
 * Use depuis Module 5 handleStatutChange.
 */
export function triggerOnStatutChange(prospect, newStatut) {
  if (newStatut === 'audite') {
    addNotification({
      type: 'audit_a_chiffrer',
      title: `${prospect.nom} a chiffrer`,
      message: `Audit valide. Cree le devis pour lancer l'offre.`,
      severity: 'info',
      navigate: { module: 'chiffrage', context: { prospectId: prospect.id } },
      dedupKey: `audit-a-chiffrer-pulse:${prospect.id}:${Date.now()}`,
      expireDays: 3,
    });
  } else if (newStatut === 'compte_ouvert') {
    addNotification({
      type: 'compte_ouvert',
      title: `Compte ouvert : ${prospect.nom}`,
      message: `+50 EUR si commande >= 1500 EUR HT. Pense au remerciement.`,
      severity: 'success',
      navigate: { module: 'redaction', context: { prospectId: prospect.id, type: 'remerciement' } },
      dedupKey: `compte-ouvert:${prospect.id}`,
      expireDays: 14,
    });
  }
}
